import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    CircularProgress,
    Typography,
    Alert,
    Fade,
    Grid,
    Paper,
    Stack,
    Button,
    Chip,
    Avatar,
    LinearProgress,
    alpha
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    FilterAlt as FilterAltIcon,
    MenuBook as MenuBookIcon,
    DashboardCustomize as CombinedIcon,
    Refresh as RefreshIcon,
    AccountBalanceWallet as WalletIcon,
    TrendingUp as TrendingUpIcon,
    PieChart as PieChartIcon,
    FlightTakeoff as FlightIcon,
    CheckCircle as CheckCircleIcon,
    Print as PrintIcon,
    TableChart as ExcelIcon,
    CalendarToday as CalendarIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { utils, writeFile } from 'xlsx';

// Redux actions pour charger toutes les données nécessaires au calcul analytique
import { fetchFactures } from '../../data/slices/factureSlice';
import { fetchBillets } from '../../data/slices/billetsSlice';
import { fetchCircuits } from '../../data/slices/circuitSlice';
import { fetchToClient } from '../../data/slices/toClientSlice';
import { fetchCompagnieVol } from '../../data/slices/compagnieVolSlice';

// Moteur analytique & Composants
import {
    generateComparativePeriods,
    calculateDashboardAnalytics
} from '../../utils/dashboardAnalyticsEngine';

import PageHeader from '../../components/layout/PageHeader';
import { COLORS } from '../../settings/themeSettings';
import { formatCurrency } from '../../utils/compteClientEngine';

import DashboardCriteriaModal from './components/DashboardCriteriaModal';
import DashboardAnalysisModal, { ANALYSES_LIST } from './components/DashboardAnalysisModal';
import DashboardCombinedView from './components/DashboardCombinedView';
import DashboardDocumentViewer from './components/DashboardDocumentViewer';
import DashboardChartsView from './components/DashboardChartsView';
import './TabbedDashboard.css';

const ECHELON_OPTIONS = [
    { value: 'Trimestre', label: 'Trimestres' },
    { value: 'Mois', label: 'Mois' },
    { value: 'Semestre', label: 'Semestres' },
    { value: 'Date', label: 'Dates libres' }
];

const TabbedDashboard = () => {
    const dispatch = useDispatch();

    // ─── État Global Redux ──────────────────────────────────────────────────
    const factures = useSelector((state) => state.factures?.factures || []);
    const planeTickets = useSelector((state) => state.billets?.billets || []);
    const circuits = useSelector((state) => state.circuits?.circuits || []);
    const toClients = useSelector((state) => state.toClients?.toClients || []);
    const compagnieVols = useSelector((state) => state.compagnieVols?.compagnieVols || []);

    const [loadingData, setLoadingData] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // ─── État des Critères & Modes ──────────────────────────────────────────
    const [criteria, setCriteria] = useState({
        startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
        endDate: dayjs().endOf('year').format('YYYY-MM-DD'),
        echelonnement: 'Trimestre',
        typeActivite: 'Tous'
    });

    const [selectedAnalysisId, setSelectedAnalysisId] = useState('doc1');
    const [isCriteriaModalOpen, setIsCriteriaModalOpen] = useState(false);
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [isCombinedMode, setIsCombinedMode] = useState(false);

    // Getter pour l'export Excel
    const [exportDataGetter, setExportDataGetter] = useState(null);

    // ─── Chargement initial des données métier ──────────────────────────────
    const loadAllData = useCallback(async () => {
        setLoadingData(true);
        try {
            await Promise.allSettled([
                dispatch(fetchFactures()).unwrap?.(),
                dispatch(fetchBillets()).unwrap?.(),
                dispatch(fetchCircuits()).unwrap?.(),
                dispatch(fetchToClient()).unwrap?.(),
                dispatch(fetchCompagnieVol()).unwrap?.()
            ]);
            setLoadingData(false);
        } catch (err) {
            console.error("Erreur lors du chargement des données analytiques:", err);
            setLoadingData(false);
            setLoadError("Impossible de charger l'intégralité des données en temps réel.");
        }
    }, [dispatch]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // ─── Calcul Analytique sur 4 Périodes ───────────────────────────────────
    const rawData = useMemo(() => ({
        factures,
        planeTickets,
        circuits,
        toClients,
        compagnieVols
    }), [factures, planeTickets, circuits, toClients, compagnieVols]);

    // Génération des 4 périodes comparatives
    const periods = useMemo(() => {
        return generateComparativePeriods(
            criteria.echelonnement,
            criteria.startDate,
            criteria.endDate
        );
    }, [criteria.echelonnement, criteria.startDate, criteria.endDate]);

    // Calcul des données analytiques
    const analytics = useMemo(() => {
        return calculateDashboardAnalytics(rawData, periods, criteria.typeActivite);
    }, [rawData, periods, criteria.typeActivite]);

    // Titre de l'analyse en cours
    const currentAnalysis = useMemo(() => {
        return ANALYSES_LIST.find(a => a.id === selectedAnalysisId) || ANALYSES_LIST[0];
    }, [selectedAnalysisId]);

    // ─── Calcul des KPI globaux (4 cartes statistiques) ──────────────────────
    const statsSummary = useMemo(() => {
        if (!analytics || !Array.isArray(analytics.periodData)) {
            return { totalCA: 0, totalMarge: 0, avgPctMarge: 0, totalBilletterie: 0, totalTourisme: 0, pctBilletterie: 0, pctTourisme: 0 };
        }

        let totalCA = 0;
        let totalMarge = 0;
        let totalBilletterie = 0;
        let totalTourisme = 0;

        analytics.periodData.forEach(p => {
            if (p && !p.isEmpty) {
                totalCA += Number(p.global?.ca || 0);
                totalMarge += Number(p.global?.marge || 0);
                totalBilletterie += Number(p.billetterie?.ca || 0);
                totalTourisme += Number(p.tourisme?.ca || 0);
            }
        });

        const avgPctMarge = totalCA > 0 ? (totalMarge / totalCA) * 100 : 0;
        const pctBilletterie = totalCA > 0 ? Math.round((totalBilletterie / totalCA) * 100) : 0;
        const pctTourisme = totalCA > 0 ? Math.round((totalTourisme / totalCA) * 100) : 0;

        return {
            totalCA,
            totalMarge,
            avgPctMarge,
            totalBilletterie,
            totalTourisme,
            pctBilletterie,
            pctTourisme
        };
    }, [analytics]);

    // ─── Export Excel direct ────────────────────────────────────────────────
    const handleExportExcel = () => {
        if (!exportDataGetter) {
            window.alert("Aucune donnée tabulaire à exporter.");
            return;
        }
        try {
            const { title, periods: exportPeriods, rows } = exportDataGetter();
            const excelRows = [
                ['AFRICA FOR TOURISM - TRAVELMANAGER'],
                ['TABLEAU DE BORD COMPARATIF DES VENTES ET RÉSULTATS'],
                [`Analyse : ${title}`],
                [`Période : ${criteria.startDate} au ${criteria.endDate} (${criteria.echelonnement})`],
                [`Date d'extraction : ${new Date().toLocaleString('fr-FR')}`],
                [],
                ['Intitulé', 'Formules', ...(exportPeriods || []).map(p => p.label.replace('\n', ' '))]
            ];

            (rows || []).forEach(r => {
                if (r.isSectionHeader) {
                    excelRows.push([r.label.toUpperCase(), '', '', '', '', '']);
                } else if (r.isSubSectionHeader) {
                    excelRows.push([`  * ${r.label} *`, '', '', '', '', '']);
                } else {
                    excelRows.push([
                        (r.isIndent ? '    ' : '') + (r.label || ''),
                        r.formula || '',
                        r.values?.[0] !== undefined ? r.values[0] : '',
                        r.values?.[1] !== undefined ? r.values[1] : '',
                        r.values?.[2] !== undefined ? r.values[2] : '',
                        r.values?.[3] !== undefined ? r.values[3] : ''
                    ]);
                }
            });

            const ws = utils.aoa_to_sheet(excelRows);
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws, 'Synthèse Activité');
            const safeTitle = (title || 'tableau_de_bord').toLowerCase().replace(/[^a-z0-9]/g, '_');
            writeFile(wb, `AFT_${safeTitle}_${dayjs().format('YYYY-MM-DD')}.xlsx`);
        } catch (e) {
            console.error("Erreur export Excel:", e);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // ─── Gestionnaires de navigation entre écrans ───────────────────────────
    const handleConfirmCriteria = (newCriteria) => {
        setCriteria(newCriteria);
        setIsCriteriaModalOpen(false);
        setIsAnalysisModalOpen(true);
    };

    const handleOpenCombinedFromCriteria = (newCriteria) => {
        if (newCriteria) setCriteria(newCriteria);
        setIsCriteriaModalOpen(false);
        setIsCombinedMode(true);
    };

    const handleSelectAnalysis = (analysisId) => {
        setSelectedAnalysisId(analysisId);
        setIsAnalysisModalOpen(false);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3.5 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* PAGE HEADER (HARMONISÉ AVEC GESTION CLIENT & TOUT LE PROGICIEL)    */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <PageHeader
                title="Tableau de bord"
                subtitle="États comparatifs de rentabilité, billetterie, tourisme et ventilation analytique"
                icon={DashboardIcon}
                breadcrumbs={[
                    { label: 'Accueil', path: '/' },
                    { label: 'Tableaux de bord', path: '/etat/dashboard' }
                ]}
                actions={[
                    {
                        label: 'Critères de période',
                        icon: <FilterAltIcon />,
                        onClick: () => setIsCriteriaModalOpen(true),
                        variant: 'contained',
                        color: 'primary'
                    },
                    {
                        label: 'Catalogue d\'analyses',
                        icon: <MenuBookIcon />,
                        onClick: () => setIsAnalysisModalOpen(true),
                        variant: 'outlined',
                        color: 'primary'
                    },
                    {
                        label: isCombinedMode ? 'Vue Standard' : 'Vue Combinée (+)',
                        icon: <CombinedIcon />,
                        onClick: () => setIsCombinedMode(!isCombinedMode),
                        variant: isCombinedMode ? 'contained' : 'outlined',
                        color: 'secondary'
                    },
                    {
                        label: 'Actualiser',
                        icon: <RefreshIcon />,
                        onClick: loadAllData,
                        variant: 'outlined',
                        color: 'inherit'
                    }
                ]}
            />

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* 4 CARTES KPI STATISTIQUES GLOBALES                                */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {/* 1. Chiffre d'affaires Global */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.2,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'white',
                            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)' }
                        }}
                    >
                        <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Chiffre d'affaires total
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                                {formatCurrency(statsSummary.totalCA)} <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>CFA</span>
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                {periods.length} périodes comparatives ({criteria.echelonnement})
                            </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary, width: 48, height: 48 }}>
                            <WalletIcon />
                        </Avatar>
                    </Paper>
                </Grid>

                {/* 2. Marge Brute Globale */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.2,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'white',
                            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)' }
                        }}
                    >
                        <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Marge brute totale
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#16a34a', mt: 0.5 }}>
                                {formatCurrency(statsSummary.totalMarge)} <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>CFA</span>
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#16a34a', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontWeight: 600 }}>
                                <CheckCircleIcon sx={{ fontSize: 13 }} /> Résultat brut d'activité
                            </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: '#dcfce7', color: '#16a34a', width: 48, height: 48 }}>
                            <TrendingUpIcon />
                        </Avatar>
                    </Paper>
                </Grid>

                {/* 3. Taux de Marge Moyen */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.2,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'white',
                            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)' }
                        }}
                    >
                        <Box sx={{ width: 'calc(100% - 56px)' }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Taux de marge moyen
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#7c3aed', mt: 0.5 }}>
                                {statsSummary.avgPctMarge.toFixed(1)} %
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, Math.max(0, statsSummary.avgPctMarge))}
                                    sx={{
                                        flex: 1,
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: '#ede9fe',
                                        '& .MuiLinearProgress-bar': { bgcolor: '#7c3aed', borderRadius: 3 }
                                    }}
                                />
                            </Box>
                        </Box>
                        <Avatar sx={{ bgcolor: '#ede9fe', color: '#7c3aed', width: 48, height: 48 }}>
                            <PieChartIcon />
                        </Avatar>
                    </Paper>
                </Grid>

                {/* 4. Répartition Billetterie / Tourisme */}
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.2,
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'white',
                            boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.08)' }
                        }}
                    >
                        <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Billetterie vs Tourisme
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0284c7', mt: 0.5 }}>
                                {statsSummary.pctBilletterie}% <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>/</span> {statsSummary.pctTourisme}%
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                                Billet: {formatCurrency(statsSummary.totalBilletterie)} | Tour: {formatCurrency(statsSummary.totalTourisme)}
                            </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0284c7', width: 48, height: 48 }}>
                            <FlightIcon />
                        </Avatar>
                    </Paper>
                </Grid>
            </Grid>

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* CARTE DE SÉLECTION RAPIDE DE DOCUMENT (DOC 1 À 6) & FILTRES RAPIDES */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <Paper
                elevation={0}
                className="no-print"
                sx={{
                    p: 2.5,
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    bgcolor: 'white',
                    boxShadow: '0 4px 20px -2px rgba(0,0,0,0.03)',
                    mb: 3
                }}
            >
                {/* Ligne 1 : Sélecteur 1-clic des 6 analyses */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.2 }}>
                        Sélection du document d'analyse :
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                        {ANALYSES_LIST.map((doc) => {
                            const isSelected = selectedAnalysisId === doc.id;
                            return (
                                <Button
                                    key={doc.id}
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    onClick={() => setSelectedAnalysisId(doc.id)}
                                    startIcon={doc.id === 'graphique' ? <PieChartIcon /> : <AssessmentIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: isSelected ? 800 : 600,
                                        fontSize: '0.82rem',
                                        py: 0.8,
                                        px: 1.8,
                                        borderRadius: '10px',
                                        bgcolor: isSelected ? '#0f172a !important' : '#f8fafc',
                                        color: isSelected ? '#ffffff !important' : '#334155',
                                        borderColor: isSelected ? '#0f172a' : '#e2e8f0',
                                        boxShadow: isSelected ? '0 4px 12px rgba(15, 23, 42, 0.25)' : 'none',
                                        '&:hover': {
                                            bgcolor: isSelected ? '#1e293b !important' : '#f1f5f9',
                                            borderColor: isSelected ? '#1e293b' : '#cbd5e1'
                                        }
                                    }}
                                >
                                    {doc.title}
                                </Button>
                            );
                        })}
                    </Stack>
                </Box>

                {/* Ligne 2 : Filtres rapides & actions directes */}
                <Box sx={{ pt: 1.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    {/* Échelonnement & Activité */}
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                Échelonnement :
                            </Typography>
                            {ECHELON_OPTIONS.map((opt) => (
                                <Chip
                                    key={opt.value}
                                    label={opt.label}
                                    size="small"
                                    onClick={() => setCriteria(prev => ({ ...prev, echelonnement: opt.value }))}
                                    color={criteria.echelonnement === opt.value ? 'primary' : 'default'}
                                    variant={criteria.echelonnement === opt.value ? 'filled' : 'outlined'}
                                    sx={{
                                        fontWeight: criteria.echelonnement === opt.value ? 700 : 500,
                                        cursor: 'pointer',
                                        bgcolor: criteria.echelonnement === opt.value ? '#2563eb' : '#ffffff',
                                        borderColor: criteria.echelonnement === opt.value ? '#2563eb' : '#cbd5e1'
                                    }}
                                />
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, ml: { md: 2 } }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                Activité :
                            </Typography>
                            {['Tous', 'Billetterie', 'Tourisme'].map((act) => (
                                <Chip
                                    key={act}
                                    label={act}
                                    size="small"
                                    onClick={() => setCriteria(prev => ({ ...prev, typeActivite: act }))}
                                    color={criteria.typeActivite === act ? 'secondary' : 'default'}
                                    variant={criteria.typeActivite === act ? 'filled' : 'outlined'}
                                    sx={{
                                        fontWeight: criteria.typeActivite === act ? 700 : 500,
                                        cursor: 'pointer'
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Badge de période active */}
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, bgcolor: '#f1f5f9', px: 1.5, py: 0.4, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <CalendarIcon sx={{ fontSize: 15, color: '#64748b' }} />
                            <Typography variant="caption" sx={{ color: '#334155', fontWeight: 600 }}>
                                {dayjs(criteria.startDate).format('DD/MM/YYYY')} ➔ {dayjs(criteria.endDate).format('DD/MM/YYYY')}
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Actions d'export & impression */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                color: '#334155',
                                borderColor: '#cbd5e1',
                                '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                            }}
                        >
                            Imprimer
                        </Button>

                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<ExcelIcon />}
                            onClick={handleExportExcel}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                bgcolor: '#16a34a',
                                '&:hover': { bgcolor: '#15803d' }
                            }}
                        >
                            Exporter Excel
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {/* Message d'erreur éventuel */}
            {loadError && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
                    {loadError}
                </Alert>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* ÉCRAN 3 : MODE COMBINÉ (SI ACTIVÉ)                                */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {isCombinedMode && (
                <Fade in={isCombinedMode}>
                    <Box sx={{ mb: 3 }} className="no-print">
                        <DashboardCombinedView 
                            criteria={criteria}
                            onCriteriaChange={(newCrit) => setCriteria(newCrit)}
                            selectedAnalysisId={selectedAnalysisId}
                            onSelectAnalysis={(id) => setSelectedAnalysisId(id)}
                            onCloseCombined={() => setIsCombinedMode(false)}
                            onRefresh={() => loadAllData()}
                        />
                    </Box>
                </Fade>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* LOADER OU CONTENU PRINCIPAL DE L'ANALYSE                           */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {loadingData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 350, gap: 2 }}>
                    <CircularProgress color="primary" />
                    <Typography variant="body2" color="text.secondary">
                        Calcul et agrégation des indicateurs sur les 4 périodes comparatives...
                    </Typography>
                </Box>
            ) : (
                <Box>
                    {/* DOC 1 À 5 : RENDU DES DOCUMENTS COMPARATIFS AVEC EN-TÊTES FIGÉS */}
                    {selectedAnalysisId !== 'graphique' ? (
                        <DashboardDocumentViewer 
                            analysisId={selectedAnalysisId}
                            analysisTitle={currentAnalysis.title}
                            periods={analytics.periods}
                            periodData={analytics.periodData}
                            allCompagnies={analytics.allCompagnies}
                            allClients={analytics.allClients}
                            criteria={criteria}
                            onRegisterExportGetter={setExportDataGetter}
                        />
                    ) : (
                        /* 6.6 ANALYSE GRAPHIQUE */
                        <DashboardChartsView 
                            periods={analytics.periods}
                            periodData={analytics.periodData}
                            allCompagnies={analytics.allCompagnies}
                            allClients={analytics.allClients}
                        />
                    )}
                </Box>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* MODALE ÉCRAN 1 : SÉLECTION DES CRITÈRES (SAGE 100)                */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <DashboardCriteriaModal 
                open={isCriteriaModalOpen}
                onClose={() => setIsCriteriaModalOpen(false)}
                onConfirmCriteria={handleConfirmCriteria}
                onOpenCombined={handleOpenCombinedFromCriteria}
                initialCriteria={criteria}
            />

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* MODALE ÉCRAN 2 : CATALOGUE DE CHOIX DE L'ANALYSE                  */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <DashboardAnalysisModal 
                open={isAnalysisModalOpen}
                onClose={() => setIsAnalysisModalOpen(false)}
                onSelectAnalysis={handleSelectAnalysis}
                initialAnalysisId={selectedAnalysisId}
            />
        </Box>
    );
};

export default TabbedDashboard;
